import type { NodeRef } from "../api/types";

export interface Point {
  x: number;
  y: number;
}

// Deterministic PRNG (mulberry32) so a given graph always lays out identically —
// no jitter between renders, and copied links restore the same picture.
function rng(seed: number) {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Force-directed layout for the whole-graph map (Fruchterman–Reingold style):
 * repulsion between every pair, spring attraction along edges, a gentle pull to
 * the center, and an extra pull toward each node's community centroid so related
 * code clusters — the organic constellation the aggregate map wants, not a grid.
 * Pure and deterministic. Positions are returned in their own coordinate space;
 * the canvas fits them to its viewBox, so the absolute scale here does not matter.
 */
export function forceLayout(
  nodes: NodeRef[],
  edges: [string, string][],
  communityOf: (id: string) => number,
): Record<string, Point> {
  const n = nodes.length;
  const pos: Record<string, Point> = {};
  if (n === 0) return pos;

  const rand = rng(n * 2654435761 + edges.length);
  const idx = new Map<string, number>();
  nodes.forEach((node, i) => idx.set(node.id, i));

  // Seed on community rings so the simulation starts already loosely grouped.
  const commList = [...new Set(nodes.map((nd) => communityOf(nd.id)))].sort((a, b) => a - b);
  const commAngle = new Map<number, number>();
  commList.forEach((c, i) => commAngle.set(c, (i / commList.length) * Math.PI * 2));
  const R = 360 + Math.sqrt(n) * 30;
  const px = new Float64Array(n);
  const py = new Float64Array(n);
  nodes.forEach((node, i) => {
    const base = commAngle.get(communityOf(node.id)) ?? 0;
    const a = base + (rand() - 0.5) * 1.1;
    const r = R * (0.25 + rand() * 0.75);
    px[i] = Math.cos(a) * r;
    py[i] = Math.sin(a) * r;
  });

  const eIdx: [number, number][] = [];
  for (const [s, t] of edges) {
    const si = idx.get(s);
    const ti = idx.get(t);
    if (si !== undefined && ti !== undefined && si !== ti) eIdx.push([si, ti]);
  }

  const area = (R * 2) * (R * 2);
  const k = Math.sqrt(area / n); // ideal edge length
  const k2 = k * k * 1.7; // stronger repulsion -> more spread, fewer overlaps
  const iterations = n > 600 ? 240 : 360;
  let temp = R * 0.6;
  const cool = temp / (iterations + 1);
  const dx = new Float64Array(n);
  const dy = new Float64Array(n);

  // Precompute community centroids each tick for the clustering pull.
  const commOf = nodes.map((nd) => communityOf(nd.id));

  for (let it = 0; it < iterations; it++) {
    dx.fill(0);
    dy.fill(0);

    // Repulsion: every pair pushes apart (F = k^2 / d).
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        let ddx = px[i] - px[j];
        let ddy = py[i] - py[j];
        let d2 = ddx * ddx + ddy * ddy;
        if (d2 < 0.01) { ddx = rand() - 0.5; ddy = rand() - 0.5; d2 = 0.01; }
        const f = k2 / d2;
        const d = Math.sqrt(d2);
        const fx = (ddx / d) * f;
        const fy = (ddy / d) * f;
        dx[i] += fx; dy[i] += fy;
        dx[j] -= fx; dy[j] -= fy;
      }
    }

    // Attraction along edges (F = d^2 / k).
    for (const [a, b] of eIdx) {
      const ddx = px[a] - px[b];
      const ddy = py[a] - py[b];
      const d = Math.sqrt(ddx * ddx + ddy * ddy) || 0.01;
      const f = (d * d) / k;
      const fx = (ddx / d) * f;
      const fy = (ddy / d) * f;
      dx[a] -= fx; dy[a] -= fy;
      dx[b] += fx; dy[b] += fy;
    }

    // Community centroids → gentle cohesion so areas read as clusters.
    const cx = new Map<number, number>();
    const cy = new Map<number, number>();
    const cc = new Map<number, number>();
    for (let i = 0; i < n; i++) {
      const c = commOf[i];
      cx.set(c, (cx.get(c) ?? 0) + px[i]);
      cy.set(c, (cy.get(c) ?? 0) + py[i]);
      cc.set(c, (cc.get(c) ?? 0) + 1);
    }

    for (let i = 0; i < n; i++) {
      const c = commOf[i];
      const count = cc.get(c) ?? 1;
      const ccx = (cx.get(c) ?? 0) / count;
      const ccy = (cy.get(c) ?? 0) / count;
      dx[i] += (ccx - px[i]) * 0.035; // gentle pull toward community centroid
      dy[i] += (ccy - py[i]) * 0.035;
      dx[i] += -px[i] * 0.01; // light pull toward the global center
      dy[i] += -py[i] * 0.01;

      // Integrate with temperature-capped displacement.
      const disp = Math.sqrt(dx[i] * dx[i] + dy[i] * dy[i]) || 0.01;
      const limited = Math.min(disp, temp);
      px[i] += (dx[i] / disp) * limited;
      py[i] += (dy[i] / disp) * limited;
    }
    temp = Math.max(temp - cool, R * 0.02);
  }

  nodes.forEach((node, i) => (pos[node.id] = { x: px[i], y: py[i] }));
  return pos;
}

/** Degree (in + out) per node id, for sizing hubs larger. */
export function degreeMap(edges: [string, string][]): Map<string, number> {
  const deg = new Map<string, number>();
  for (const [s, t] of edges) {
    deg.set(s, (deg.get(s) ?? 0) + 1);
    deg.set(t, (deg.get(t) ?? 0) + 1);
  }
  return deg;
}
